import { useState, useEffect } from 'react';
import { Plus, FolderGit2, Trash2, Edit2, Loader2, Github } from 'lucide-react';
import { getProjects, createProject, updateProject, deleteProject } from '../../services/projectsApi';
import type { Project } from '../../services/projectsApi';

export function AdminProjects() {
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        status: 'En cours',
        stars: '0',
        forks: '0',
        github: '',
        tech: '' as string // splitted by commas
    });

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getProjects();
            setProjects(data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur réseau');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (project?: Project) => {
        if (project) {
            setShowForm(true);
            setEditingId(project.id);
            setFormData({
                name: project.name,
                description: project.description,
                status: project.status,
                stars: (project.stars || 0).toString(),
                forks: (project.forks || 0).toString(),
                github: project.github || '',
                tech: (project.tech || []).join(', ')
            });
        } else {
            setShowForm(true);
            setEditingId(null);
            setFormData({
                name: '',
                description: '',
                status: 'En cours',
                stars: '0',
                forks: '0',
                github: '',
                tech: ''
            });
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingId(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError(null);

            const projData = {
                name: formData.name,
                description: formData.description,
                status: formData.status,
                stars: parseInt(formData.stars, 10) || 0,
                forks: parseInt(formData.forks, 10) || 0,
                github: formData.github,
                tech: formData.tech.split(',').map(s => s.trim()).filter(Boolean)
            };

            if (editingId) {
                await updateProject(editingId, projData);
            } else {
                await createProject(projData);
            }

            await fetchProjects();
            setShowForm(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Voulez-vous vraiment supprimer ce projet ?')) return;

        try {
            setLoading(true);
            await deleteProject(id);
            await fetchProjects();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
            setLoading(false);
        }
    };

    if (loading && projects.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#1f75cb]" />
            </div>
        );
    }

    return (
        <div className="space-y-6 flex flex-col items-center max-w-5xl mx-auto">
            <div className="flex justify-between items-center w-full">
                <h1 className="text-2xl font-bold text-[#1d1d1f] flex items-center gap-2">
                    <FolderGit2 className="w-8 h-8 text-[#1f75cb]" />
                    Projets
                </h1>
                <button
                    onClick={() => handleOpenForm()}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Ajouter
                </button>
            </div>

            {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-100 w-full">
                    {error}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-6">
                            {editingId ? 'Modifier le projet' : 'Nouveau projet'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du projet *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="Ex: Cloud Manager"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                <textarea
                                    required
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20 min-h-[80px]"
                                    placeholder="Courte description..."
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.status}
                                        onChange={e => setFormData({ ...formData, status: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Ex: En cours"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Stars Github</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.stars}
                                        onChange={e => setFormData({ ...formData, stars: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Forks Github</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={formData.forks}
                                        onChange={e => setFormData({ ...formData, forks: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Lien Github</label>
                                <input
                                    type="url"
                                    value={formData.github}
                                    onChange={e => setFormData({ ...formData, github: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="https://github.com/..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Technologies (séparées par une virgule)</label>
                                <input
                                    type="text"
                                    value={formData.tech}
                                    onChange={e => setFormData({ ...formData, tech: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="Docker, Kubernetes, Go..."
                                />
                            </div>

                            <div className="flex justify-end gap-3 mt-8">
                                <button
                                    type="button"
                                    onClick={handleCloseForm}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors flex items-center justify-center font-medium disabled:opacity-50"
                                >
                                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {projects.length === 0 && !loading && !error ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-100 w-full">
                    <FolderGit2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun projet n'est configuré pour le moment.</p>
                </div>
            ) : (
                <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {projects.map(proj => (
                        <div key={proj.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative group flex flex-col h-full">
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleOpenForm(proj)} className="p-1.5 bg-blue-50 text-blue-600 rounded-md">
                                    <Edit2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDelete(proj.id)} className="p-1.5 bg-red-50 text-red-600 rounded-md">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="flex gap-2 items-center mb-2">
                                <h3 className="font-bold text-gray-900 leading-tight pr-16">{proj.name}</h3>
                                <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-xs font-semibold">{proj.status}</span>
                            </div>
                            
                            <p className="text-sm text-gray-600 my-2 flex-grow">{proj.description}</p>
                            
                            <div className="flex flex-wrap gap-1.5 my-3">
                                {proj.tech.map((t, idx) => (
                                    <span key={idx} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                                        {t}
                                    </span>
                                ))}
                            </div>

                            <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                                <div className="flex gap-3">
                                    <span>Stars: {proj.stars || 0}</span>
                                    <span>Forks: {proj.forks || 0}</span>
                                </div>
                                {proj.github && (
                                    <a href={proj.github} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-[#1d1d1f]">
                                        <Github className="w-4 h-4" /> Github
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
